/**
 * Stage D — Simplified Pause: one question, "Why are you stopping work?".
 *
 * The answer is the canonical Pause intent (pauseIntent.ts). It already says
 * whether the advisor or a manager must respond, so there is no separate
 * "Also kick to FOH?" question and no second reason list. Diagnostic results
 * are optional here: an unfinished diagnosis is the normal case at a pause.
 *
 * Presentational only, and the same on desktop, tablet and phone: the commit
 * goes through the Stage A executor into the server's pause_my_work.
 */
import { useState, type ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  PAUSE_REASON_GROUPS,
  PAUSE_REASON_OPTIONS,
  pauseOption,
  type PauseDetail,
  PARTS_WAIT_REASONS,
  composePartsNote,
  partsPauseProblem,
  type PartsWaitReason,
} from '@/lib/timer/interaction/pauseIntent';

export interface PauseWorkDialogProps {
  open: boolean;
  busy: boolean;
  detail: PauseDetail | '';
  note: string;
  onDetailChange: (detail: PauseDetail) => void;
  onNoteChange: (note: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  /** Optional findings for diagnostic jobs in this technician's work (null: none). */
  diagnosticSection: ReactNode | null;
}

export function PauseWorkDialog({
  open, busy, detail, note, onDetailChange, onNoteChange, onConfirm, onCancel, diagnosticSection,
}: PauseWorkDialogProps) {
  const [showFindings, setShowFindings] = useState(false);
  const chosen = pauseOption(detail);
  // OCT28 #7: a parts wait says WHY (required) and explains (optional; required for "Other"); sent as the note.
  const [partsReason, setPartsReason] = useState<PartsWaitReason | null>(null);
  const [partsExplanation, setPartsExplanation] = useState('');
  const isParts = chosen?.detail === 'waiting_parts';
  const partsProblem = isParts ? partsPauseProblem(partsReason, partsExplanation) : null;
  const setParts = (reason: PartsWaitReason | null, explanation: string) => {
    setPartsReason(reason); setPartsExplanation(explanation); onNoteChange(composePartsNote(reason, explanation));
  };
  const reset = () => { setShowFindings(false); setPartsReason(null); setPartsExplanation(''); };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onCancel(); } }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="pause-work-dialog">
        <DialogHeader>
          <DialogTitle>Why are you stopping work?</DialogTitle>
          <DialogDescription>
            OptiBay tells whoever needs to respond. Your timer pauses; other technicians' work is unaffected.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3" role="radiogroup" aria-label="Why are you stopping work?">
          {PAUSE_REASON_GROUPS.map((g) => (
            <div key={g.title} className="space-y-1">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{g.title}</p>
              <div className="grid gap-1">
                {PAUSE_REASON_OPTIONS.filter((o) => g.intents.includes(o.intent)).map((o) => (
                  <button
                    key={o.detail}
                    type="button"
                    role="radio"
                    aria-checked={detail === o.detail}
                    disabled={busy}
                    data-testid={`pause-reason-${o.detail}`}
                    onClick={() => {
                      // OCT28 #7: leaving the parts answer drops its reason and note.
                      if (isParts && o.detail !== 'waiting_parts') setParts(null, '');
                      onDetailChange(o.detail);
                    }}
                    className={cn(
                      'rounded-md border px-3 py-2 text-left text-sm transition-colors',
                      detail === o.detail ? 'border-primary bg-primary/10 font-medium' : 'hover:bg-muted',
                    )}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {isParts && (
          <div className="space-y-2" data-testid="pause-parts-section">
            <p className="text-sm font-medium">Why is the work waiting on parts?</p>
            <div className="grid grid-cols-1 gap-1 sm:grid-cols-2" role="radiogroup" aria-label="Why is the work waiting on parts?">
              {PARTS_WAIT_REASONS.map((r) => (
                <button key={r} type="button" role="radio" aria-checked={partsReason === r} disabled={busy}
                  data-testid={`pause-parts-reason-${r.replace(/[^A-Za-z]+/g, '-').toLowerCase()}`}
                  onClick={() => setParts(r, partsExplanation)}
                  className={cn('min-h-11 rounded-md border px-3 py-2 text-left text-sm md:min-h-9',
                    partsReason === r ? 'border-primary bg-primary/10 font-medium' : 'hover:bg-muted')}>
                  {r}
                </button>
              ))}
            </div>
            <label htmlFor="pause-parts-explanation" className="text-sm text-muted-foreground">
              {partsReason === 'Other' ? 'What is the parts problem?' : 'Explain (which part, what is wrong, any order or ETA) — optional'}
            </label>
            <Textarea id="pause-parts-explanation" data-testid="pause-parts-explanation" value={partsExplanation}
              maxLength={450} rows={2} disabled={busy} onChange={(e) => setParts(partsReason, e.target.value)} />
            <p className="text-xs text-muted-foreground">The front office is told. The car stays where it is, and nothing is rescheduled.</p>
            {partsProblem && <p className="text-xs text-muted-foreground" data-testid="pause-parts-problem">{partsProblem}</p>}
          </div>
        )}

        {chosen?.offersNote && !isParts && (
          <div className="space-y-1">
            <label htmlFor="pause-note" className="text-sm text-muted-foreground">
              {chosen.intent === 'ADVISOR' ? 'What does the advisor need to know? (optional)'
                : chosen.intent === 'TECH_HELP' ? 'What do you need help with? (optional)'
                : 'Details (optional)'}
            </label>
            <Textarea
              id="pause-note"
              data-testid="pause-note"
              value={note}
              maxLength={500}
              rows={2}
              disabled={busy}
              onChange={(e) => onNoteChange(e.target.value)}
            />
          </div>
        )}

        {diagnosticSection && (
          <div className="border-t pt-2">
            {showFindings ? diagnosticSection : (
              <button
                type="button"
                className="text-sm text-primary underline-offset-2 hover:underline"
                data-testid="pause-record-findings"
                onClick={() => setShowFindings(true)}
              >
                Finished a diagnosis or inspection? Record the results (optional)
              </button>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onCancel(); }} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={!chosen || busy || partsProblem !== null} data-testid="pause-confirm">
            {busy ? 'Pausing…' : 'Pause'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
